/**
 * AI Model Settings Component
 * User Story 1: Configure AI Model Preferences
 * Tasks: T020-T024
 */

import { useReducer, useCallback, useEffect } from 'react';
import { useAiModelPreferences } from '@/hooks/useAiModelPreferences';
import { Id } from '../../../convex/_generated/dataModel';
import { toast } from 'sonner';

interface IAiModelSettingsProps {
  businessId: Id<'businesses'> | undefined;
}

type ModelPrefsState = {
  useClaude: boolean;
  useGPT: boolean;
  useGemini: boolean;
};

type ModelPrefsAction =
  | { type: 'TOGGLE_MODEL'; payload: { model: 'claude' | 'gpt' | 'gemini'; value: boolean } }
  | { type: 'SYNC_FROM_SERVER'; payload: ModelPrefsState };

function modelPrefsReducer(state: ModelPrefsState, action: ModelPrefsAction): ModelPrefsState {
  switch (action.type) {
    case 'TOGGLE_MODEL': {
      const { model, value } = action.payload;
      return {
        useClaude: model === 'claude' ? value : state.useClaude,
        useGPT: model === 'gpt' ? value : state.useGPT,
        useGemini: model === 'gemini' ? value : state.useGemini,
      };
    }
    case 'SYNC_FROM_SERVER':
      return action.payload;
  }
}

export function AiModelSettings({ businessId }: IAiModelSettingsProps) {
  const { preferences, isLoading, updatePreferences } = useAiModelPreferences(businessId);

  const [modelPrefs, dispatch] = useReducer(modelPrefsReducer, {
    useClaude: preferences?.useClaude ?? true,
    useGPT: preferences?.useGPT ?? false,
    useGemini: preferences?.useGemini ?? false,
  });
  const { useClaude, useGPT, useGemini } = modelPrefs;

  // Sync from server when preferences change. Using a derived key as dep so the
  // effect only fires when the actual values change, not on every render.
  const serverKey = preferences
    ? `${preferences.useClaude}-${preferences.useGPT}-${preferences.useGemini}`
    : null;
  useEffect(() => {
    if (!serverKey || !preferences) return;
    dispatch({
      type: 'SYNC_FROM_SERVER',
      payload: {
        useClaude: preferences.useClaude,
        useGPT: preferences.useGPT,
        useGemini: preferences.useGemini,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- serverKey encodes all three values; preferences object itself is unstable
  }, [serverKey]);

  // Auto-save handler - event-driven, no effect needed
  const handleToggleModel = useCallback(
    async (model: 'claude' | 'gpt' | 'gemini', newValue: boolean) => {
      // Calculate new state
      const newState: ModelPrefsState = {
        useClaude: model === 'claude' ? newValue : useClaude,
        useGPT: model === 'gpt' ? newValue : useGPT,
        useGemini: model === 'gemini' ? newValue : useGemini,
      };

      // Validation: At least one model must be selected
      if (!newState.useClaude && !newState.useGPT && !newState.useGemini) {
        toast.error('At least one AI model must be selected');
        return;
      }

      // Optimistic update via single dispatch
      dispatch({ type: 'TOGGLE_MODEL', payload: { model, value: newValue } });

      // Auto-save to backend
      try {
        await updatePreferences({
          useClaude: newState.useClaude,
          useGPT: newState.useGPT,
          useGemini: newState.useGemini,
          claudeModel: newState.useClaude ? 'claude-opus-4-6' : undefined,
          gptModel: newState.useGPT ? 'gpt-5.4' : undefined,
          geminiModel: newState.useGemini ? 'gemini-3.1-pro-preview' : undefined,
        });

        toast.success('Preferences saved');
      } catch (error: unknown) {
        console.error('Failed to save preferences:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to save preferences');
        // Revert on error
        dispatch({ type: 'TOGGLE_MODEL', payload: { model, value: !newValue } });
      }
    },
    [useClaude, useGPT, useGemini, updatePreferences]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-sm text-neutral-500">Loading preferences...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Model Selection Checkboxes */}
      <div className="space-y-4">
        {/* Claude */}
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={useClaude}
            onChange={e => handleToggleModel('claude', e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary"
          />
          <div className="flex-1">
            <div className="font-medium">Claude Opus 4.6</div>
            <div className="text-sm text-neutral-600">
              Anthropic's most capable model, exceptional for creative and complex content
            </div>
            {useClaude && (
              <div className="text-xs text-neutral-500 mt-1">Model: claude-opus-4-6</div>
            )}
          </div>
        </label>

        {/* GPT-5 */}
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={useGPT}
            onChange={e => handleToggleModel('gpt', e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary"
          />
          <div className="flex-1">
            <div className="font-medium">GPT-5.4</div>
            <div className="text-sm text-neutral-600">
              OpenAI's most capable model, great for structured and professional content
            </div>
            {useGPT && <div className="text-xs text-neutral-500 mt-1">Model: gpt-5.4</div>}
          </div>
        </label>

        {/* Gemini */}
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={useGemini}
            onChange={e => handleToggleModel('gemini', e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary"
          />
          <div className="flex-1">
            <div className="font-medium">Gemini 3.1 Pro</div>
            <div className="text-sm text-neutral-600">
              Google's most advanced reasoning model for complex tasks
            </div>
            {useGemini && (
              <div className="text-xs text-neutral-500 mt-1">Model: gemini-3.1-pro-preview</div>
            )}
          </div>
        </label>
      </div>

      {/* Usage Stats */}
      {preferences && (
        <div className="p-4 bg-neutral-50 rounded-lg space-y-2">
          <div className="text-sm font-medium">Usage This Month</div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-neutral-600">Single-model generations</div>
              <div className="font-semibold">{preferences.singleModelGenerationsThisMonth}</div>
            </div>
            <div>
              <div className="text-neutral-600">Multi-model generations</div>
              <div className="font-semibold">{preferences.multiModelGenerationsThisMonth}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
